import { ProgramSettings } from "@domain/models/ProgramSettings";
import React, {
    type FC,
    type PropsWithChildren,
    type ContextType,
    createContext,
    use,
    useEffect,
    useMemo,
    useState,
} from "react";
import { type IViewsKeys } from "@domain/models/User";
import { UserContext } from "@domain/context/UserProvider";

export const UserSettingsContext = createContext({
    disabledColumns: new Map<keyof IViewsKeys, string[]>(),
    onChangeDisabledColumns: (_view: keyof IViewsKeys, _disabledColumns: string[]) => {},
});

export const UserSettingsProvider: FC<PropsWithChildren> = (props) => {
    const [disabledColumns, setDisabledColumns] = useState<Map<keyof IViewsKeys, string[]>>(new Map());
    const [documentVisibility, setDocumentVisibility] = useState(true);

    const { fireUser, selectedCompany } = use(UserContext);
    const userData = useMemo(() => {
        if (!fireUser || !selectedCompany) {
            return null;
        }

        return {
            userId: fireUser.uid,
            companyId: selectedCompany,
        };
    }, [fireUser, selectedCompany]);

    useEffect(() => {
        const handleVisibilityChange = function () {
            setDocumentVisibility(document.visibilityState === "visible");
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);

    useEffect(() => {
        if (!userData) {
            return;
        }

        if (!documentVisibility) {
            return;
        }

        ProgramSettings.getColumns(userData).then(setDisabledColumns);
    }, [userData, documentVisibility]);

    const settingsValue: ContextType<typeof UserSettingsContext> = useMemo(
        () => ({
            disabledColumns,
            onChangeDisabledColumns: (view: keyof IViewsKeys, columns: string[]) => {
                disabledColumns.set(view, columns);
                setDisabledColumns(new Map(disabledColumns));
                ProgramSettings.updateColumns(userData, view, columns).then((r) => r);
            },
        }),
        [disabledColumns, userData],
    );

    return <UserSettingsContext value={settingsValue}>{props.children}</UserSettingsContext>;
};
